// Level/world data. Worlds catalog lives in worlds.js — this module exposes
// a `LEVELS` array (for backwards-compat with existing UI code) plus the
// endless-wave scaler.
import { WORLDS, getWorld, WORLD_COUNT } from "./worlds";

function worldToLevel(w) {
  return {
    id: w.id,
    name: w.name,
    subtitle: w.subtitle,
    mapSize: w.mapSize,
    goal: w.goal,
    time: w.time,
    zombies: { ...w.zombies },
    boss: !!w.boss,
    theme: w.theme,
    // Legacy flat colours kept so existing renderers don't need a rewrite.
    skyColor: w.colors.sky,
    fogColor: w.colors.fog,
    groundColor: w.colors.ground,
    accentColor: w.colors.accent,
    // Phase 6 extras — read by Game.jsx/MapScene.jsx where supported.
    fogNear: w.fog?.near,
    fogFar: w.fog?.far,
    ambientIntensity: w.ambient?.intensity,
    ambientColor: w.ambient?.color,
    props: w.props,
    pacing: w.pacing || { spawnIntervalMult: 1, speedMult: 1 },
  };
}

export const LEVELS = WORLDS.map(worldToLevel);

export const ZOMBIE_TYPES = {
  regular: { color: "#7ed957", speed: 1.2, size: 1.0, hp: 1.0, coins: 1, score: 10 },
  fast:    { color: "#9be36b", speed: 2.4, size: 0.9, hp: 1.0, coins: 2, score: 20 },
  big:     { color: "#5fb53a", speed: 0.7, size: 1.6, hp: 3.0, coins: 3, score: 30 },
  glowing: { color: "#c2ff5d", speed: 1.5, size: 1.0, hp: 1.0, coins: 5, score: 50, glow: true },
  boss:    { color: "#3fa322", speed: 1.0, size: 2.6, hp: 8.0, coins: 25, score: 250, glow: true, boss: true },
};

export function getLevel(id) {
  const w = getWorld(id);
  return worldToLevel(w);
}

// Endless scaling: each wave (loop through all worlds) ramps difficulty.
// Wave 1 = base values. Each wave above adds zombies, speeds them up, and
// shortens the timer slightly. World-specific `pacing` is also applied so
// faster-pacing worlds (Neon City, Carnival) feel snappier at every wave.
export function getScaledLevel(id, wave = 1) {
  const base = getLevel(id);
  const w = Math.max(1, wave);
  const k = w - 1; // how many waves above base
  const zMul = 1 + k * 0.35; // +35% zombies per wave
  const tMul = Math.max(0.55, 1 - k * 0.08); // -8% time per wave, floor 55%
  const speedBoost = (1 + k * 0.12) * (base.pacing?.speedMult || 1);
  return {
    ...base,
    goal: Math.round(base.goal * zMul),
    time: Math.round(base.time * tMul),
    zombies: {
      regular: Math.round((base.zombies.regular || 0) * zMul),
      fast: Math.round((base.zombies.fast || 0) * zMul),
      big: Math.round((base.zombies.big || 0) * zMul),
      glowing: Math.round((base.zombies.glowing || 0) * zMul),
    },
    wave: w,
    waveSpeedBoost: speedBoost,
  };
}

export { WORLD_COUNT };
